import { useMemo } from "react";
import { MultiMetricPicker } from "@/components/MetricPicker";
import { fmtMetricValue, getMetric } from "@/lib/metrics";
import {
  countryName,
  flagFor,
  parseNumberOrZero,
} from "@/lib/overview-helpers";
import type { ByCountryRow } from "@/lib/types";
import { ByCountryModeToggle } from "./ByCountryModeToggle";
import { CustomizeButton } from "./CustomizeButton";

export function ByCountryCard({
  rows,
  loading,
  metricIds,
  onMetricsChange,
  mode,
  onModeChange,
}: {
  rows: ByCountryRow[];
  loading: boolean;
  metricIds: string[];
  onMetricsChange: (ids: string[]) => void;
  mode: "country" | "prediction";
  onModeChange: (m: "country" | "prediction") => void;
}) {
  const prepared = useMemo(() => {
    const list = rows
      // Drop countries where every selected metric is zero (or null).
      // Per user feedback — if spend/installs/trials are all zero for a
      // country, it's noise: showing a long tail of empty rows makes
      // real data harder to skim. Null counts as empty too (gated
      // metric).
      .filter((r) =>
        metricIds.some((id) => {
          const v = getMetric(id).fromByApp(r);
          return v != null && v !== 0;
        })
      )
      .map((r) => {
        // The metric registry reads ByAppRow shape. ByCountryRow has
        // the same numeric fields — pass it through directly; the
        // shared `ByDimensionRow` type in metrics.ts covers the
        // signature.
        const values = metricIds.map((id) => getMetric(id).fromByApp(r));
        const spendTotal = parseNumberOrZero(r.spend_usd);
        return { ...r, values, spendTotal };
      });
    const totalSpend = list.reduce((s, r) => s + r.spendTotal, 0) || 1;
    return list
      .map((r) => ({ ...r, share: r.spendTotal / totalSpend }))
      .sort((a, b) => b.spendTotal - a.spendTotal)
      // Cap at 30 rows: the long tail of low-volume storefronts just
      // slows the widget down without adding insight. Users can still
      // reach deeper data via Ads Manager's country filter.
      .slice(0, 30);
  }, [rows, metricIds]);

  const metricLabels = metricIds.map((id) => getMetric(id).short).join(" / ");

  return (
    // No hover-reveal here: users kept missing the pencil because it
    // was invisible until you moved the mouse onto the card. The
    // customize button is now always rendered in the card header —
    // clicking still opens the same MultiMetricPicker.
    <div className="card">
      <div className="card-head" style={{ alignItems: "center" }}>
        <h3>Performance by country</h3>
        <div
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Pencil + labels first so the iOS toggle pins to the
             trailing edge — matches its position in prediction
             mode, keeping the click target stationary across
             mode flips. */}
          <span className="muted">{metricLabels}</span>
          <MultiMetricPicker
            values={metricIds}
            onChange={onMetricsChange}
            min={1}
            max={4}
            title="Columns on this widget"
            trigger={({ toggle, open }) => (
              <CustomizeButton active={open} onClick={toggle} />
            )}
          />
          <ByCountryModeToggle value={mode} onChange={onModeChange} />
        </div>
      </div>
      <div style={{ padding: "4px 0 8px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `1fr repeat(${metricIds.length}, minmax(72px, auto))`,
            padding: "6px 16px 4px",
            gap: 14,
            fontSize: 10.5,
            color: "var(--c-text-4)",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            borderBottom: "1px solid var(--c-divider)",
          }}
        >
          <div>Country</div>
          {metricIds.map((id) => (
            <div key={id} style={{ textAlign: "right" }}>
              {getMetric(id).short}
            </div>
          ))}
        </div>
        {loading && prepared.length === 0 && (
          <div
            className="muted"
            style={{
              padding: "20px 16px",
              textAlign: "center",
              fontSize: 13,
            }}
          >
            Loading…
          </div>
        )}
        {!loading && prepared.length === 0 && (
          <div
            className="muted"
            style={{
              padding: "20px 16px",
              textAlign: "center",
              fontSize: 13,
            }}
          >
            No country-level activity in this period.
          </div>
        )}
        {/* Up to 5 rows visible at once; beyond that, scroll inside the
            card so the rest of the dashboard stays still. 51px-per-row
            matches the row padding + content height below (10px top +
            10px bottom + ~31px content). Capped at 30 total rows to
            avoid choking on long tails of storefronts with trickle
            activity. */}
        <div
          style={{
            maxHeight: prepared.length > 5 ? 5 * 51 : undefined,
            overflowY: prepared.length > 5 ? "auto" : undefined,
          }}
        >
          {prepared.map((r, i) => (
            <div
              key={r.country || "__unknown__"}
              style={{
                display: "grid",
                gridTemplateColumns: `1fr repeat(${metricIds.length}, minmax(72px, auto))`,
                alignItems: "center",
                padding: "10px 16px",
                gap: 14,
                borderBottom:
                  i < prepared.length - 1 ? "1px solid var(--c-divider)" : "0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: "var(--c-surface-sunk)",
                    fontSize: 14,
                    display: "grid",
                    placeItems: "center",
                    flex: "none",
                  }}
                  aria-hidden
                >
                  {flagFor(r.country)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 500,
                      fontSize: 13,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {countryName(r.country)}
                  </div>
                  <div className="bar-track" style={{ marginTop: 4 }}>
                    <div
                      className="bar-fill"
                      style={{ right: `${(1 - r.share) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
              {metricIds.map((id, col) => (
                <div
                  key={id}
                  className="mono"
                  style={{ textAlign: "right", fontSize: 12.5 }}
                >
                  {fmtMetricValue(id, r.values[col] ?? null)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
